import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  isRead: boolean;
  avatar?: string;
  actionText?: string;
  actionUrl?: string;
}

@Component({
  selector: 'app-notification-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <div [class]="'notification-item ' + (notification().isRead ? 'read' : 'unread')" (click)="onNotificationClick()">
      <!-- Notification Indicator -->
      <div [class]="'notification-indicator ' + notification().type"></div>

      <!-- Avatar/Icon Container -->
      <div class="notification-avatar">
        @if (notification().avatar) {
          <img [src]="notification().avatar" [alt]="notification().title" class="avatar-image" />
        } @else {
          <div [class]="'avatar-icon ' + notification().type">
            <i [class]="getTypeIcon()"></i>
          </div>
        }
      </div>

      <!-- Content -->
      <div class="notification-content">
        <div class="notification-header">
          <h4 class="notification-title">{{ notification().title }}</h4>
          <span class="notification-time">{{ getTimeAgo() }}</span>
        </div>

        <p class="notification-message">{{ notification().message }}</p>

        @if (notification().actionText && notification().actionUrl) {
          <button class="notification-action" (click)="onActionClick($event)" type="button">
            {{ notification().actionText }}
            <i class="fas fa-chevron-right"></i>
          </button>
        }
      </div>

      <!-- Mark as read button -->
      @if (!notification().isRead) {
        <button class="mark-read-btn" (click)="onMarkAsRead($event)" aria-label="Marcar como leída" type="button">
          <i class="fas fa-circle"></i>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .notification-item {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: var(--space-md);
        padding: var(--space-lg);
        border-bottom: 1px solid var(--border-muted);
        cursor: pointer;
        transition: all 0.2s ease;
        background: var(--bg-primary);
      }

      .notification-item:hover {
        background: var(--bg-secondary);
        transform: translateX(2px);
      }

      .notification-item.unread {
        background: linear-gradient(90deg, var(--bg-accent) 0%, var(--bg-primary) 100%);
        border-left: 3px solid var(--red-500);
      }

      .notification-item.read {
        opacity: 0.7;
      }

      .notification-item.read:hover {
        opacity: 1;
      }

      /* Notification Indicator */
      .notification-indicator {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      .notification-item.unread .notification-indicator {
        opacity: 1;
      }

      .notification-indicator.info {
        background: var(--blue-500);
      }
      .notification-indicator.success {
        background: var(--green-500);
      }
      .notification-indicator.warning {
        background: var(--orange-500);
      }
      .notification-indicator.error {
        background: var(--red-500);
      }

      /* Avatar */
      .notification-avatar {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }

      .avatar-icon {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-size: var(--text-lg);
        color: var(--text-inverse);
      }

      .avatar-icon.info {
        background: var(--blue-500);
      }
      .avatar-icon.success {
        background: var(--green-500);
      }
      .avatar-icon.warning {
        background: var(--orange-500);
      }
      .avatar-icon.error {
        background: var(--red-500);
      }

      /* Content */
      .notification-content {
        flex: 1;
        min-width: 0;
      }

      .notification-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: var(--space-sm);
        margin-bottom: var(--space-xs);
      }

      .notification-title {
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        line-height: var(--leading-snug);
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
      }

      .notification-time {
        font-size: var(--text-xs);
        color: var(--text-muted);
        font-weight: var(--font-medium);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .notification-message {
        font-size: var(--text-sm);
        color: var(--text-secondary);
        line-height: var(--leading-relaxed);
        margin: 0 0 var(--space-sm) 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .notification-action {
        display: inline-flex;
        align-items: center;
        gap: var(--space-xs);
        padding: var(--space-xs) var(--space-sm);
        background: var(--bg-tertiary);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius-md);
        color: var(--red-600);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
      }

      .notification-action:hover {
        background: var(--bg-accent);
        border-color: var(--border-accent);
        color: var(--red-700);
        transform: translateX(2px);
      }

      .notification-action i {
        font-size: 10px;
        transition: transform 0.2s ease;
      }

      .notification-action:hover i {
        transform: translateX(2px);
      }

      /* Mark as read button */
      .mark-read-btn {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--red-500);
        cursor: pointer;
        border-radius: 50%;
        transition: all 0.2s ease;
        font-size: 8px;
      }

      .mark-read-btn:hover {
        background: var(--bg-accent);
        color: var(--red-600);
        transform: scale(1.2);
      }

      .mark-read-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      /* Responsive adjustments */
      @media (max-width: 480px) {
        .notification-item {
          padding: var(--space-md);
          gap: var(--space-sm);
        }

        .notification-avatar {
          width: 32px;
          height: 32px;
        }

        .avatar-icon {
          font-size: var(--text-base);
        }

        .notification-title {
          font-size: var(--text-xs);
        }

        .notification-message {
          font-size: var(--text-xs);
        }

        .notification-action {
          padding: var(--space-xs);
          font-size: 10px;
        }
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .notification-item,
        .notification-action,
        .mark-read-btn {
          transition: none;
        }

        .notification-item:hover {
          transform: none;
        }

        .notification-action:hover {
          transform: none;
        }

        .notification-action:hover i {
          transform: none;
        }

        .mark-read-btn:hover {
          transform: none;
        }
      }
    `,
  ],
})
export class NotificationItemComponent {
  // Input properties
  notification = input.required<Notification>();

  // Output events
  notificationClick = output<Notification>();
  markAsRead = output<string>();
  actionClick = output<{ notificationId: string; actionUrl: string }>();

  // Methods
  onNotificationClick(): void {
    this.notificationClick.emit(this.notification());
  }

  onMarkAsRead(event: Event): void {
    event.stopPropagation();
    this.markAsRead.emit(this.notification().id);
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
    const notification = this.notification();
    if (notification.actionUrl) {
      this.actionClick.emit({
        notificationId: notification.id,
        actionUrl: notification.actionUrl,
      });
    }
  }

  getTypeIcon(): string {
    const iconMap = {
      info: 'fas fa-info-circle',
      success: 'fas fa-check-circle',
      warning: 'fas fa-exclamation-triangle',
      error: 'fas fa-times-circle',
    };
    return iconMap[this.notification().type];
  }

  getTimeAgo(): string {
    const now = new Date();
    const notification = this.notification();
    const diffInMinutes = Math.floor((now.getTime() - notification.timestamp.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  }
}
